import { useState } from 'react';
import { useT } from '@/store';
import { useDwellStatus, useForceCloseDwellRecord } from '@/hooks/useTenant';
import { Panel } from '@/components/ui/Panel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';

/**
 * The worklist for stops nobody ever closed.
 *
 * A dwell record open longer than the engine's plausibility bound is not a
 * truck waiting anywhere — it is a row whose departure was never recorded.
 * Until this existed the console had only one way to say that: the same red
 * "over allowance" tag a live 21-minute queue gets, re-pushed every sixty
 * seconds for as long as the row stayed open. A stop abandoned over a
 * weekend produced roughly 3,800 identical alarms, and the 21-minute case —
 * the one somebody can still act on — was indistinguishable in the middle
 * of them.
 *
 * So it is taken out of the alarm and put here instead, with the count and
 * the bound stated (the same cap-and-disclose accounting the Prastovų Aktas
 * publishes with excluded_stop_count) and with the one action that resolves
 * it. Silently ageing the record out was the alternative and it is worse:
 * the row is evidence that a status transition never happened, and the
 * order behind it is still sitting open.
 *
 * Renders nothing when there are none. A console is read continuously and
 * its quiet is itself the answer; that is the opposite of the Aktas, which
 * prints its exclusions even at zero because a document is read once, out
 * of context, and "none excluded" and "this document does not ask" have to
 * be distinguishable there.
 */
export function UnclosedDwellPanel() {
  const t = useT();
  const { data: dwell } = useDwellStatus();
  const forceClose = useForceCloseDwellRecord();
  const [failedId, setFailedId] = useState(null);

  const unclosed = dwell?.unclosed ?? [];
  const capHours = dwell?.dwellCapHours;
  if (unclosed.length === 0) return null;

  const close = (dwellRecordId) => {
    setFailedId(null);
    forceClose.mutate(dwellRecordId, { onError: () => setFailedId(dwellRecordId) });
  };

  return (
    <Panel title={t('dwell.unclosedTitle')}>
      <p className="text-[11px] leading-snug text-amber" role="note">
        {t('dwell.unclosedNote', { n: unclosed.length, cap: capHours ?? '—' })}
      </p>
      <ul className="mt-2.5 space-y-2">
        {unclosed.map((row) => (
          <li
            key={row.dwell_record_id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-[11px] border border-line bg-white/2 p-3"
          >
            <div className="min-w-0">
              <div className="truncate text-[12.5px] font-semibold">
                {[row.driver_name ?? t('dispatcher.unassigned'), row.license_plate]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              <div className="truncate text-[11px] text-dim">
                {[
                  row.location_name,
                  row.elapsed_minutes == null
                    ? null
                    : t('dwell.openForHours', { n: Math.round(row.elapsed_minutes / 60) }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {failedId === row.dwell_record_id ? (
                <span role="alert" className="text-[11px] text-red">
                  {t('dwell.forceCloseFailed')}
                </span>
              ) : null}
              <ConfirmButton
                label={t('dwell.forceClose')}
                confirmLabel={t('dwell.confirmForceClose')}
                hint={t('dwell.forceCloseHint')}
                disabled={forceClose.isPending}
                onConfirm={() => close(row.dwell_record_id)}
              />
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
